import type { MockMessage } from "@/dummy";

/**
 * Combine the fetched history with the session's live rows. History wins on an id collision (so
 * an SSE message that also shows up in a later fetch renders once), EXCEPT for a live row marked
 * `edited` (a realtime `messageUpdate`): its content replaces the history row's, which would
 * otherwise hide the edit. The live row's `clientKey` is carried onto the history row so the
 * AnimatePresence key stays stable across the live -> history handoff (no remount flicker).
 * An `editOnly` overlay whose original is not in history (older than the loaded page) is kept out
 * of the list: it would otherwise show up at the bottom as a brand-new message.
 */
/** Same rendered content (text, media, buttons, sources): an update that changes none of it edits nothing. */
export function sameMessageContent(a: MockMessage, b: MockMessage): boolean {
  return (
    a.text === b.text &&
    JSON.stringify(a.media ?? null) === JSON.stringify(b.media ?? null) &&
    JSON.stringify(a.buttons ?? null) === JSON.stringify(b.buttons ?? null) &&
    JSON.stringify(a.sources ?? null) === JSON.stringify(b.sources ?? null)
  );
}

export function mergeHistoryAndLive(history: MockMessage[], live: MockMessage[]): MockMessage[] {
  const liveById = new Map(live.map((m) => [m.id, m]));
  const deletedIds = new Set(live.filter((m) => m.deleted).map((m) => m.id));
  const historyStable = history
    .filter((h) => !deletedIds.has(h.id))
    .map((h) => {
      const l = liveById.get(h.id);
      if (!l) return h;
      const clientKey = h.clientKey ?? l.clientKey;
      if (l.edited && !sameMessageContent(h, l)) {
        return {
          ...h,
          text: l.text,
          media: l.media ?? h.media,
          buttons: l.buttons ?? h.buttons,
          sources: l.sources ?? h.sources,
          edited: true,
          ...(clientKey ? { clientKey } : {}),
        };
      }
      return clientKey && !h.clientKey ? { ...h, clientKey } : h;
    });
  const historyIds = new Set(history.map((m) => m.id));
  return [...historyStable, ...live.filter((m) => !historyIds.has(m.id) && !m.editOnly)];
}
